import { FC, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Box } from '@mui/system';

import ModalNotification from '@/shared/components/dialogs/ModalNotification';
import WaveUIForm_V2 from '@/shared/components/forms/WaveUIForm_V2';
import FieldType from '@/shared/components/forms/WaveUIForm_V2/types/fieldType.enum';
import BookingsContact from '@/shared/types/bookings/contact.type';

type AllowedFormValues = Pick<
  BookingsContact,
  | 'booking_service_key'
  | 'booking_contact_name'
  | 'booking_location'
  | 'timezone'
>;

type BookingCreateFormDialogProps = {
  initialFormValues: BookingsContact | null;
  timezones: string[];
  errors?: string[];
  onClose: () => void;
  onCreateContact: (
    formValues: Partial<BookingsContact & { run_auth_flow: boolean }>,
  ) => void;
  onUpdateContact: (formValues: Partial<BookingsContact>) => void;
};

const AddContactFormDialog: FC<BookingCreateFormDialogProps> = ({
  initialFormValues,
  timezones,
  errors,
  onClose,
  onCreateContact,
  onUpdateContact,
}) => {
  const { t } = useTranslation(['userSettings', 'common']);

  const [formErrors, setFormErrors] = useState<Partial<AllowedFormValues>>({});

  const [formValues, setFormValues] = useState<AllowedFormValues>({
    booking_service_key: initialFormValues?.booking_service_key ?? '',
    booking_contact_name: initialFormValues?.booking_contact_name ?? '',
    booking_location: initialFormValues?.booking_location ?? '',
    timezone: initialFormValues?.timezone ?? '',
  });

  const onValidate = () => {
    const errors: AllowedFormValues = {
      booking_service_key: '',
      booking_contact_name: '',
      booking_location: '',
      timezone: '',
    };

    if (!formValues.booking_service_key) {
      errors.booking_service_key = t(
        'userSettings:booking_service_key_required',
      );
    }

    if (!formValues.booking_contact_name) {
      errors.booking_contact_name = t(
        'userSettings:booking_contact_name_required',
      );
    }

    if (!formValues.booking_location) {
      errors.booking_location = t('userSettings:booking_location_required');
    }

    if (!formValues.timezone) {
      errors.timezone = t('userSettings:timezone_required');
    }

    setFormErrors(errors);

    return !Object.values(errors).some((error) => error);
  };

  if (errors?.length) {
    return (
      <ModalNotification
        open
        onClose={onClose}
        dialogProps={{
          title: t('common:error'),
          content: <Box sx={{ py: 1 }}>{errors.join(', ')}</Box>,
          actions: [
            {
              label: t('common:close'),
              onClick: onClose,
            },
          ],
        }}
      />
    );
  }

  return (
    <ModalNotification
      open
      onClose={onClose}
      dialogProps={{
        title: t('userSettings:addBookingContact'),
        content: (
          <Box sx={{ py: 1 }}>
            <WaveUIForm_V2
              fields={[
                {
                  field_type: FieldType.Select,
                  field_props: {
                    name: 'booking_service_key',
                    label: t('userSettings:booking_service_key'),
                    options: [{ value: 'GCP', label: 'GCP' }],
                    value: formValues.booking_service_key,
                    placeholder: t(
                      'userSettings:booking_service_key_placeholder',
                    ),
                    error: !!formErrors.booking_service_key,
                    helperText: formErrors.booking_service_key,
                  },
                },
                {
                  field_type: FieldType.Text,
                  field_props: {
                    name: 'booking_contact_name',
                    label: t('userSettings:booking_contact_name'),
                    value: formValues.booking_contact_name,
                    placeholder: t(
                      'userSettings:booking_contact_name_placeholder',
                    ),
                    error: !!formErrors.booking_contact_name,
                    helperText: formErrors.booking_contact_name,
                  },
                },
                {
                  field_type: FieldType.Text,
                  field_props: {
                    name: 'booking_location',
                    label: t('userSettings:booking_location'),
                    value: formValues.booking_location,
                    placeholder: t('userSettings:booking_location_placeholder'),
                    error: !!formErrors.booking_location,
                    helperText: formErrors.booking_location,
                  },
                },
                {
                  field_type: FieldType.Autocomplete,
                  field_props: {
                    name: 'timezone',
                    label: t('userSettings:timezone'),
                    options: timezones
                      .map((timezone) => ({
                        value: timezone,
                        label: timezone,
                        category: timezone.split('/')[0],
                      }))
                      .sort((a, b) => a.category.localeCompare(b.category)),
                    value: formValues.timezone,
                    placeholder: t('userSettings:timezone_placeholder'),
                    error: !!formErrors.timezone,
                    helperText: formErrors.timezone,
                  },
                },
              ]}
              getFieldsValues={(fieldValue) => {
                const [key, value] = Object.entries(fieldValue)[0];

                if (key === 'timezone') {
                  const newValue = value as { value: string; label: string };

                  setFormValues((prevValues) => ({
                    ...prevValues,
                    timezone: newValue.value,
                  }));

                  return;
                }

                setFormValues((prevValues) => ({
                  ...prevValues,
                  ...fieldValue,
                }));
              }}
            />
          </Box>
        ),
        actions: [
          {
            label: t('common:cancel'),
            variant: 'text',
            disabled: false,
            onClick: onClose,
          },
          initialFormValues
            ? {
                label: t('common:update'),
                variant: 'contained',
                disabled: false,
                onClick: () => {
                  const isValid = onValidate();

                  if (!isValid) return;
                  onUpdateContact(formValues);
                },
              }
            : {
                label: t('userSettings:authenticateInNewWindow'),
                variant: 'contained',
                disabled: false,
                onClick: () => {
                  const isValid = onValidate();

                  if (!isValid) return;
                  onCreateContact({ ...formValues, run_auth_flow: true });
                },
              },
        ],
      }}
    />
  );
};

export default AddContactFormDialog;
